import { useMemo, useState } from 'react'
import type { ProgressionResponse } from '../api/types'
import { formatPoints } from '../lib/decimal'
import { buildChart, MAX_HUES } from './chartGeometry'
import { useElementSize } from './useElementSize'

/**
 * How the championship stood after each round.
 *
 * Hand-written SVG rather than a charting library, for four things a library
 * fights: dimming the series you are not pointing at, end labels that drop
 * instead of stacking, one tooltip listing every series at a round, and a mark
 * for the rounds that paid points without moving the total.
 *
 * Colour comes from each competitor's final rank over the whole field, so
 * pinning or unpinning somebody never repaints the others, and past eight
 * ranks there is no ninth hue -- the tail is recessive grey with the legend
 * carrying its identity.
 */
export function ProgressionChart({
  data,
  pinned = [],
  onTogglePin,
  caption,
}: {
  data: ProgressionResponse
  pinned?: string[]
  /** Without it the series are fixed: no pinning, and the lines are not buttons. */
  onTogglePin?: (key: string) => void
  /** Replaces the default caption, for series that are not a season's field. */
  caption?: string
}) {
  const [box, size] = useElementSize<HTMLDivElement>()
  const [hover, setHover] = useState<{ index: number; x: number } | null>(null)
  const [focused, setFocused] = useState<string | null>(null)

  const height = 340
  const chart = useMemo(
    () => buildChart(data.rounds, data.series, Math.max(size.width, 320), height),
    [data, size.width],
  )

  const nearest = (clientX: number, target: SVGSVGElement) => {
    const bounds = target.getBoundingClientRect()
    const x = ((clientX - bounds.left) / bounds.width) * chart.width
    let best = 0
    chart.columns.forEach((column, index) => {
      if (Math.abs(column.x - x) < Math.abs((chart.columns[best]?.x ?? 0) - x)) best = index
    })
    return best
  }

  const hovered = hover === null ? null : chart.columns[hover.index]

  return (
    <figure className="chart" ref={box}>
      <figcaption className="chart__caption">
        {caption ??
          `Points after each round, as the championship table read at the time. ${data.rounds.length} rounds, ${data.series.length} of ${data.series_total} competitors.`}
        {chart.lines.some((line) => line.capped.length > 0)
          ? ' A hollow ring marks a round that scored points without changing the total.'
          : ''}
      </figcaption>

      <div
        className={focused === null ? 'chart__plot' : 'chart__plot chart__plot--focused'}
        role="img"
        aria-label={summary(data)}
      >
        <svg
          viewBox={`0 0 ${chart.width} ${chart.height}`}
          width="100%"
          height={height}
          onPointerMove={(event) =>
            setHover({
              index: nearest(event.clientX, event.currentTarget),
              x: chart.columns[nearest(event.clientX, event.currentTarget)]?.x ?? 0,
            })
          }
          onPointerLeave={() => setHover(null)}
        >
          {chart.yTicks.map((tick) => (
            <g key={tick.label}>
              <line
                className="chart__grid"
                x1={chart.plot.x}
                x2={chart.plot.x + chart.plot.width}
                y1={tick.position}
                y2={tick.position}
              />
              <text
                className="chart__axis"
                x={chart.plot.x - 8}
                y={tick.position + 4}
                textAnchor="end"
              >
                {tick.label}
              </text>
            </g>
          ))}

          {chart.xTicks.map((tick) => (
            <text
              key={`${tick.label}-${tick.position}`}
              className={tick.counts === false ? 'chart__axis chart__axis--out' : 'chart__axis'}
              x={tick.position}
              y={chart.plot.y + chart.plot.height + 18}
              textAnchor="middle"
            >
              {tick.label}
            </text>
          ))}

          {hovered ? (
            <line
              className="chart__crosshair"
              x1={hovered.x}
              x2={hovered.x}
              y1={chart.plot.y}
              y2={chart.plot.y + chart.plot.height}
            />
          ) : null}

          {chart.lines.map((line) => (
            <g
              key={line.key}
              className={`series series--${line.slot}${focused === line.key ? ' series--on' : ''}`}
              data-key={line.key}
            >
              <path className="series__line" d={line.path} fill="none" />
              {/* A wide transparent copy, so the line is clickable and focusable. */}
              {onTogglePin ? (
                <path
                  className="series__hit"
                  d={line.path}
                  fill="none"
                  tabIndex={0}
                  role="button"
                  aria-label={`${line.name}, ${pinned.includes(line.key) ? 'pinned' : 'not pinned'}. ${describe(line.name, data, line.key)}`}
                  onFocus={() => setFocused(line.key)}
                  onBlur={() => setFocused(null)}
                  onPointerEnter={() => setFocused(line.key)}
                  onPointerLeave={() => setFocused(null)}
                  onClick={() => onTogglePin(line.key)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      onTogglePin(line.key)
                    }
                  }}
                />
              ) : (
                <path
                  className="series__hit series__hit--static"
                  d={line.path}
                  fill="none"
                  onPointerEnter={() => setFocused(line.key)}
                  onPointerLeave={() => setFocused(null)}
                />
              )}
              {line.capped.map((dot) => (
                <circle
                  key={`capped-${dot.round}`}
                  className="series__capped"
                  cx={dot.x}
                  cy={dot.y}
                  r={4}
                />
              ))}
              {line.end ? (
                <circle className="series__end" cx={line.end.x} cy={line.end.y} r={4} />
              ) : null}
              {hovered && dotAt(line, hovered.round) ? (
                <circle
                  className="series__end"
                  cx={dotAt(line, hovered.round)!.x}
                  cy={dotAt(line, hovered.round)!.y}
                  r={4}
                />
              ) : null}
            </g>
          ))}

          {/* Labels last, so nothing is drawn over them. Ink, never the
              series colour: a light hue is illegible as text. */}
          {chart.lines.map((line) =>
            line.label ? (
              <g key={`label-${line.key}`} className={`label label--${line.slot}`}>
                {/* A leader line when the label had to move, so it still
                    points at the series it names. */}
                <polyline
                  className="label__lead"
                  fill="none"
                  points={`${line.end!.x},${line.end!.y} ${line.label.x - 7},${line.label.y} ${line.label.x - 1},${line.label.y}`}
                />
                <text className="label__text" x={line.label.x + 1} y={line.label.y + 3.5}>
                  {line.label.text}
                </text>
              </g>
            ) : null,
          )}
        </svg>

        {hovered ? (
          <Tooltip
            data={data}
            round={hovered.round}
            label={hovered.label}
            x={hovered.x}
            width={chart.width}
          />
        ) : null}
      </div>

      {data.series.length > 1 ? (
        <ul className="legend">
          {chart.lines.map((line) => (
            <li key={line.key} className={`legend__item series--${line.slot}`}>
              {onTogglePin ? (
                <button
                  type="button"
                  onClick={() => onTogglePin(line.key)}
                  onPointerEnter={() => setFocused(line.key)}
                  onPointerLeave={() => setFocused(null)}
                  aria-pressed={pinned.includes(line.key)}
                >
                  <span className="legend__key" aria-hidden="true" />
                  <span className="legend__name">{line.name}</span>
                  {line.slot === 0 ? (
                    <span className="muted"> {ordinal(line.rank)}</span>
                  ) : null}
                </button>
              ) : (
                <span
                  className="legend__static"
                  onPointerEnter={() => setFocused(line.key)}
                  onPointerLeave={() => setFocused(null)}
                >
                  <span className="legend__key" aria-hidden="true" />
                  <span className="legend__name">{line.name}</span>
                </span>
              )}
            </li>
          ))}
        </ul>
      ) : null}
    </figure>
  )
}

function dotAt(line: ReturnType<typeof buildChart>['lines'][number], round: number) {
  return line.dots.find((dot) => dot.round === round)
}

/** One tooltip for the whole round: the pointer never has to find a line. */
function Tooltip({
  data,
  round,
  label,
  x,
  width,
}: {
  data: ProgressionResponse
  round: number
  label: string
  x: number
  width: number
}) {
  const index = data.rounds.findIndex((entry) => entry.round === round)
  const rows = data.series
    .map((line, position) => ({
      key: line.key,
      name: line.name ?? line.entity,
      slot: line.rank <= MAX_HUES ? line.rank : 0,
      value: line.points[index] ?? null,
      position,
    }))
    .filter((row) => row.value !== null)
    .slice(0, 12)

  const right = x > width / 2
  return (
    <div
      className="tip"
      style={
        right ? { right: `${((width - x) / width) * 100}%` } : { left: `${(x / width) * 100}%` }
      }
    >
      <p className="tip__head">
        {label}
        <span className="muted"> round {round}</span>
      </p>
      <ul>
        {rows.map((row) => (
          <li key={row.key} className={`series--${row.slot}`}>
            <span className="tip__key" aria-hidden="true" />
            {/* Values lead, names follow: the reader has the series and wants
                the number. */}
            <strong>{formatPoints(row.value)}</strong>
            <span className="tip__name">{row.name}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

/** A sentence a screen reader can use instead of the picture. */
function summary(data: ProgressionResponse): string {
  const leader = data.series[0]
  if (leader === undefined) return 'No competitors to plot.'
  const final = leader.points.filter((value) => value !== null).at(-1)
  return (
    `Points after each of ${data.rounds.length} rounds for ${data.series.length} of ` +
    `${data.series_total} competitors. ${leader.name ?? leader.entity} leads with ` +
    `${formatPoints(final, 'no')} points.`
  )
}

function describe(name: string, data: ProgressionResponse, key: string): string {
  const line = data.series.find((entry) => entry.key === key)
  if (line === undefined) return name
  const final = line.points.filter((value) => value !== null).at(-1)
  const capped = (line.capped_rounds ?? []).length
  return (
    `Finished ${ordinal(line.rank)} on ${formatPoints(final, 'no')} points` +
    (capped > 0 ? `, with ${capped} rounds that scored without changing the total` : '') +
    '.'
  )
}

function ordinal(value: number): string {
  const suffix =
    ['th', 'st', 'nd', 'rd'][value % 100 > 10 && value % 100 < 14 ? 0 : value % 10] ?? 'th'
  return `${value}${suffix}`
}
